/**
 * Shared @mention extraction from plain text.
 * Used by CommentInput on send and streamStore as a safety net for notifications.
 *
 * .tuMetrics {"tu1": 0.88, "tu2": 0.86, "tu3": 5, "notes": "Mention ID extraction and team member resolution for notifications", "tuVersion": "2.5"}
 * .help "Parses @mentions from message text and resolves team members for notification fan-out."
 */
import type { User } from '../types';
import { extractPlainTextFromHtml } from './messageUtils';

export type MentionCandidate = {
  id: string;
  label: string;
  labelLower: string;
};

const isBoundaryChar = (ch: string) => ch === '' || /[\s.,!?;:)\]}]/.test(ch);

export function extractMentionIdsFromPlainText(
  plainText: string,
  candidates: MentionCandidate[],
): string[] {
  if (!plainText || candidates.length === 0) return [];

  const lower = plainText.toLowerCase();
  const sortedCandidates = [...candidates].sort(
    (a, b) => b.labelLower.length - a.labelLower.length,
  );
  const found = new Set<string>();

  for (let i = 0; i < lower.length; i += 1) {
    if (lower[i] !== '@') continue;

    const prevChar = i > 0 ? lower[i - 1] : '';
    if (prevChar && /[\w.]/.test(prevChar)) continue;

    const after = lower.slice(i + 1);
    for (const candidate of sortedCandidates) {
      if (!after.startsWith(candidate.labelLower)) continue;

      const endIndex = i + 1 + candidate.labelLower.length;
      const nextChar = lower[endIndex] ?? '';
      if (isBoundaryChar(nextChar)) {
        found.add(candidate.id);
        i = endIndex;
        break;
      }
    }
  }

  return Array.from(found);
}

export function buildMentionCandidates(input: {
  allUsers: User[];
  teams: Array<{ id: string; name: string }>;
  meetingSessions: Array<{ id: string; name: string }>;
  company?: { id: string; name?: string | null } | null;
}): MentionCandidate[] {
  return [
    ...input.allUsers.map((user) => ({
      id: user.id,
      label: user.name,
      labelLower: user.name.toLowerCase(),
    })),
    ...input.teams.map((team) => ({
      id: team.id,
      label: team.name,
      labelLower: team.name.toLowerCase(),
    })),
    ...input.meetingSessions.map((session) => ({
      id: session.id,
      label: session.name,
      labelLower: session.name.toLowerCase(),
    })),
    ...(input.company?.name
      ? [
          {
            id: input.company.id,
            label: input.company.name,
            labelLower: input.company.name.toLowerCase(),
          },
        ]
      : []),
  ];
}

/** Resolve user IDs for a team (primary team + private_team_memberships). */
export function getTeamMemberUserIds(
  teamId: string,
  allUsers: User[],
  options?: {
    /** When true, the sender is included (team @mentions, announcements). */
    includeSender?: boolean;
    excludeUserId?: string;
    teamName?: string;
    companyId?: string | null;
  },
): string[] {
  const includeSender = options?.includeSender ?? false;
  const excludeUserId = includeSender ? undefined : options?.excludeUserId;

  let memberIds: string[];

  // Company team is company-wide — everyone in the org, not just primary/private roster rows.
  if (options?.teamName === 'Company' && options?.companyId) {
    memberIds = allUsers
      .filter((u) => u.companyId === options.companyId)
      .map((u) => u.id);
  } else {
    memberIds = allUsers
      .filter((u) => u.team === teamId || u.privateTeams?.includes(teamId))
      .map((u) => u.id);
  }

  if (excludeUserId) {
    return memberIds.filter((id) => id !== excludeUserId);
  }
  return memberIds;
}

/** Extract mention IDs from message HTML/plain content. */
export function extractMentionIdsFromContent(
  content: string,
  candidates: MentionCandidate[],
): string[] {
  const plain = extractPlainTextFromHtml(content);
  return extractMentionIdsFromPlainText(plain, candidates);
}

export function truncateMessagePreview(text: string, maxLength = 160): string {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
}
